/** Shared client-side contracts for the configurator and API responses. */

export type View = 'single' | 'batch'

export type ModelOption = {
  label: string
  codes: string[]
  description: string
  model_group?: string
  model_description?: string
  order_count?: number
}

export type ConfigRow = {
  color: string | null
  interior: string | null
  rim: string | null
  trim: string | null
  quantity: number
  weight_share: number
}

export type StepConf = {
  step: string
  ess: number
  confidence: number
  rim_from_package: boolean
}

export type Block = {
  rank: number
  packages: string[]
  single_options: string[]
  base_options: string[]
  rim_from_package: boolean
  trim_from_package: boolean
  rows: ConfigRow[]
  n_cars: number
  weight_share: number
  overall_confidence: number
  confidence_label: 'HIGH' | 'MEDIUM' | 'LOW'
  step_confidences: StepConf[]
  violations: string[]
  nmc_model_code: string | null
  nmc_edition_code: string | null
  nmc_edition_name: string | null
  nmc_extra_packages: string[]
  nmc_included?: string[]
  source?: 'model' | 'user'
  pin?: number | null
}

export type DataQuality = {
  total_orders: number
  bto_orders: number
  bto_ratio: number
  effective_bto_weight: number
  low_bto: boolean
}

export type PredictResponse = {
  model_codes: string[]
  model_group: string
  n_cars: number
  overall_confidence: number
  confidence_label: 'HIGH' | 'MEDIUM' | 'LOW'
  blocks: Block[]
  data_quality: DataQuality
  option_descriptions: Record<string, string>
  package_contents?: Record<string, string[]>
}

export type AvailableConfig = {
  color: string | null
  interior: string | null
  rim: string | null
  trim: string | null
  weight_share: number
  count: number
}

export type ConfigValue = { code: string; description: string | null; count: number }
export type AvailableValues = {
  colors: ConfigValue[]
  interiors: ConfigValue[]
  rims: ConfigValue[]
  trims: ConfigValue[]
}

export type PackageOption = {
  code: string
  name: string | null
  count: number
  nmc_model_code: string | null
  nmc_edition_code: string | null
  nmc_edition_name: string | null
}

export type EditionOption = {
  nmc_model_code: string
  edition_code: string | null
  edition_name: string | null
}

export type NewBlockSpec = {
  packages: string[]
  nmc_model_code: string | null
  nmc_edition_code: string | null
  nmc_edition_name: string | null
  nmc_extra_packages: string[]
  rows: { color: string | null; interior: string | null; rim: string | null; trim: string | null; weight_share: number }[]
  weight_share: number
}

export type BlacklistDim = 'color' | 'interior' | 'rim' | 'trim' | 'package' | 'single_option' | 'model_code'
export type BlacklistTerm = { dimension: BlacklistDim; value: string }
export type BlacklistRule = { terms: BlacklistTerm[] }
export type VocabEntry = { code: string; description: string | null; count: number }
export type Vocabulary = Record<BlacklistDim, VocabEntry[]>

export type EditLine = {
  id: string
  blockRank: number
  packages: string[]
  color: string | null
  interior: string | null
  rim: string | null
  trim: string | null
  weight: number
  weight_share: number
  pin: number | null
  source: 'model' | 'user'
}

export type RefSuggestion = {
  model_group: string
  label: string
  codes: string[]
  order_count: number
  score: number
  structural_similarity: number
  same_class: boolean
}

export const DEFAULTS = {
  blockCoverageTarget: 0.85,
  minRowShare: 0.05,
  essThreshold: 50,
  singleOptionThreshold: 0.40,
  pkgWeight: 2.0,
  colorWeight: 1.0,
  interiorWeight: 0.8,
  rimWeight: 0.5,
  btoVsBtsBalance: 0.3,
  recencyHalfLifeDays: 180,
  enableOptionClusters: true,
  optionMinSignatureShare: 0.10,
  optionCoverageTarget: 0.75,
  maxOptionBlocksPerBundle: 3,
  minBlockShare: 0.03,
  optionCrossWeight: 0.30,
}

export type Params = typeof DEFAULTS

export function lineId(rank: number, row: ConfigRow): string {
  return `${rank}|${row.color ?? ''}|${row.interior ?? ''}|${row.rim ?? ''}|${row.trim ?? ''}`
}

export function buildLines(result: PredictResponse): EditLine[] {
  return result.blocks.flatMap(block => block.rows.map(row => ({
    id: lineId(block.rank, row),
    blockRank: block.rank,
    packages: block.packages,
    color: row.color,
    interior: row.interior,
    rim: row.rim,
    trim: row.trim,
    weight: row.quantity,
    weight_share: row.weight_share,
    pin: null,
    source: 'model' as const,
  })))
}
